import { CLIENT_CACHE_TTL_MS } from '../lib/constants';

/**
 * Cache client in-memory partagé (20 min TTL). Clé = URL + query string.
 *
 * Pas de localStorage : les listes peuvent faire plusieurs Mo → reste en
 * RAM du tab uniquement. À la navigation inter-pages, le cache survit
 * (module-level) ; à la fermeture du tab, il est purgé.
 *
 * Purge active si besoin via `clearClientCache()` (ex: après une mutation
 * détectée via WebSocket).
 */

interface CacheEntry<T> {
  data: T;
  ts: number;
}

const cache = new Map<string, CacheEntry<unknown>>();

export async function cachedFetch<T>(
  url: string,
  params: Record<string, string | string[] | undefined>,
  init: RequestInit = {},
): Promise<T> {
  const queryString = buildQueryString(params);
  const cacheKey = `${url}?${queryString}`;
  const cached = cache.get(cacheKey) as CacheEntry<T> | undefined;

  if (cached && Date.now() - cached.ts < CLIENT_CACHE_TTL_MS) {
    return cached.data;
  }

  const res = await fetch(`${url}${queryString ? `?${queryString}` : ''}`, {
    ...init,
    headers: {
      Accept: 'application/json',
      ...(init.headers as Record<string, string> | undefined),
    },
  });
  if (!res.ok) {
    const text = await res.text().catch(() => '');
    throw new Error(`${res.status}: ${text || res.statusText}`);
  }
  const data = (await res.json()) as T;
  cache.set(cacheKey, { data, ts: Date.now() });
  return data;
}

function buildQueryString(params: Record<string, string | string[] | undefined>): string {
  const parts: string[] = [];
  for (const [key, value] of Object.entries(params)) {
    if (value === undefined || value === '') continue;
    if (Array.isArray(value)) {
      for (const v of value) {
        if (v !== undefined && v !== '')
          parts.push(`${encodeURIComponent(key)}=${encodeURIComponent(v)}`);
      }
    } else {
      parts.push(`${encodeURIComponent(key)}=${encodeURIComponent(value)}`);
    }
  }
  return parts.join('&');
}

export function clearClientCache(): void {
  cache.clear();
}
